import React, { useEffect, useMemo, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import moment from 'moment';
import { RootState } from '../../reducers';
import * as projectDeletionActions from '../../actions/projectDeletions';
import * as clientDeletionActions from '../../actions/clientDeletions';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import MikaTable, { MikaColumn, MikaAction } from '../../components/MikaTable/MikaTable';
import { formatCascadeCountsLine } from './projectDeletionHelpers';

interface Props {
    intl: any;
    history: any;
    permissions: any;
    projectDeletionActions: any;
    clientDeletionActions: any;
    /**
     * Called after a successful restore so the parent can refetch its own data
     * (e.g. ContactsListPage refetches dashboard-stats to update the strip count).
     */
    onMutate?: () => void;
}

/**
 * Inline view of soft-deleted *items* (projects + clients) with a per-row restore action.
 *
 * Rendered by ContactsListPage when `stripFilter === 'recentlyDeleted'`. Fetches two
 * endpoints in parallel — `/project-deletions` and `/client-deletions` — merges by
 * `deleted_at desc`, and lets the user restore either kind. Restoring a client also
 * restores every project that was rolled into that client-cascade (handled server-side
 * by ClientDeletionService::restoreClient).
 *
 * Each row carries a `type` field ('project' | 'client') that drives both display
 * (icon + label in the Type column, fallback values in the Project column) and
 * action routing (which restore endpoint to call).
 *
 * Permission is enforced at three layers, all aligned on `superAdmin OR fullPrivileges`
 * (same audience that can perform the destructive delete in the first place):
 *   1. Strip card visibility — ContactsListPage gates by either perm,
 *   2. Backend routes — `permission:superAdmin,fullPrivileges` middleware (OR semantics),
 *   3. Defensive bail-out below if somehow reached without either perm.
 */
const RecentlyDeletedProjects: React.FC<Props> = (props) => {
    const fm = (id: string, defaultMessage: string) => props.intl.formatMessage({ id, defaultMessage });

    const [records, setRecords] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [pendingRestore, setPendingRestore] = useState<any | null>(null);
    const [restoringId, setRestoringId] = useState<string | null>(null);

    useEffect(() => {
        fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRecords = () => {
        setLoading(true);
        // Fetch both lists in parallel. axiosCaller returns the full AxiosResponse so
        // the array of resource items lives at res.data.data.
        Promise.all([
            props.projectDeletionActions.getProjectDeletions({ status: 'active' }),
            props.clientDeletionActions.getClientDeletions({ status: 'active' }),
        ]).then(([projectRes, clientRes]: any[]) => {
            const projects = Array.isArray(projectRes?.data?.data) ? projectRes.data.data : [];
            const clients = Array.isArray(clientRes?.data?.data) ? clientRes.data.data : [];
            // Defensive — backend resources should already include `type`, but stamp it
            // so dedup keys and switches below are always reliable.
            const stamped = [
                ...projects.map((r: any) => ({ ...r, type: 'project' })),
                ...clients.map((r: any) => ({ ...r, type: 'client' })),
            ];
            // Sort newest-first across both kinds.
            stamped.sort((a: any, b: any) => {
                const ad = a.deleted_at ? new Date(a.deleted_at).getTime() : 0;
                const bd = b.deleted_at ? new Date(b.deleted_at).getTime() : 0;
                return bd - ad;
            });
            setRecords(stamped);
        }).finally(() => setLoading(false));
    };

    const handleRestoreClick = (record: any) => {
        setPendingRestore(record);
    };

    const confirmRestore = () => {
        if (!pendingRestore) return;
        const record = pendingRestore;
        // Composite key — same numeric id could exist across project_deletions and
        // client_deletions, so namespace the in-flight marker by type.
        const key = `${record.type}:${record.id}`;
        setPendingRestore(null);
        setRestoringId(key);

        const restoreCall = record.type === 'client'
            ? props.clientDeletionActions.restoreClientDeletion(record.id)
            : props.projectDeletionActions.restoreProjectDeletion(record.id);

        restoreCall.then((res: any) => {
            if (res?.data?.data) {
                toast.success(fm('contact.recentlyDeleted.restoreSuccess', 'Restored.'));
                fetchRecords();
                if (props.onMutate) props.onMutate();
            }
        }).finally(() => setRestoringId(null));
    };

    const cancelRestore = () => setPendingRestore(null);

    // Icons used in the Type column so the user can spot a client row at a glance.
    const projectIcon = (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
        </svg>
    );
    const clientIcon = (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    );

    const columns: MikaColumn[] = useMemo(() => [
        {
            key: 'type',
            title: fm('contact.recentlyDeleted.col.type', 'Type'),
            sortable: true,
            render: (_value: any, row: any) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: row.type === 'client' ? '#0ea5e9' : '#64748b' }}>
                    {row.type === 'client' ? clientIcon : projectIcon}
                    {row.type === 'client'
                        ? fm('contact.recentlyDeleted.type.client', 'Client')
                        : fm('contact.recentlyDeleted.type.project', 'Project')}
                </span>
            ),
        },
        {
            key: 'projectId',
            title: fm('contact.recentlyDeleted.col.projectId', '#'),
            sortable: true,
            // For project rows: the project's id. For client rows: the client's id.
            render: (_value: any, row: any) => (row.type === 'client' ? row.client?.id : row.project?.id) ?? '—',
        },
        {
            key: 'client',
            title: fm('contact.recentlyDeleted.col.client', 'Client'),
            sortable: true,
            render: (_value: any, row: any) => row.client?.name || '—',
        },
        {
            key: 'projectName',
            title: fm('contact.recentlyDeleted.col.project', 'Project'),
            sortable: true,
            // For project rows: the project's name. For client rows: there are 0–N
            // projects rolled in — show the count instead, or em-dash if none.
            render: (_value: any, row: any) => {
                if (row.type === 'client') {
                    const projectsCount = row.affected_counts?.projects || 0;
                    return projectsCount > 0
                        ? <FormattedMessage id="contact.cascadeItem.projects" defaultMessage="{count, plural, one {# project} other {# projects}}" values={{ count: projectsCount }} />
                        : '—';
                }
                return row.project?.name || '—';
            },
        },
        {
            key: 'createdBy',
            title: fm('contact.recentlyDeleted.col.createdBy', 'Created by'),
            sortable: true,
            render: (_value: any, row: any) => (row.type === 'client' ? row.client?.created_by?.name : row.project?.created_by?.name) || '—',
        },
        {
            key: 'deletedBy',
            title: fm('contact.recentlyDeleted.col.deletedBy', 'Deleted by'),
            sortable: true,
            render: (_value: any, row: any) => row.deleted_by?.name || row.deleted_by?.email || '—',
        },
        {
            key: 'deletedAt',
            title: fm('contact.recentlyDeleted.col.deletedAt', 'Deleted at'),
            sortable: true,
            render: (_value: any, row: any) => row.deleted_at ? moment(row.deleted_at).format('DD/MM/YYYY HH:mm') : '—',
        },
        {
            key: 'includes',
            title: fm('contact.recentlyDeleted.col.includes', 'Includes'),
            render: (_value: any, row: any) => formatCascadeCountsLine(row.affected_counts) || <span style={{ color: '#94a3b8' }}>—</span>,
        },
        {
            key: 'note',
            title: fm('contact.recentlyDeleted.col.note', 'Reason'),
            // Operator-supplied reason captured at delete time (optional). The
            // `title` attribute exposes the full text on hover for any row whose
            // note got truncated by the column width, so audits don't need to
            // open a detail drawer for short notes.
            render: (_value: any, row: any) => {
                const note = row.note;
                if (!note) return <span style={{ color: '#94a3b8' }}>—</span>;
                return (
                    <span
                        title={note}
                        style={{
                            display: 'inline-block',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                        }}
                    >
                        {note}
                    </span>
                );
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [props.intl]);

    const tableActions: MikaAction[] = useMemo(() => [
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
            ),
            tooltip: fm('contact.recentlyDeleted.restore', 'Restore'),
            onClick: (row: any) => {
                const key = `${row.type}:${row.id}`;
                if (restoringId !== key) handleRestoreClick(row);
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [restoringId, props.intl]);

    // Confirm-modal copy adapts to whether you're restoring a project or a client.
    const restoreConfirmTitle = pendingRestore?.type === 'client'
        ? fm('contact.recentlyDeleted.confirmRestore.titleClient', 'Restore client?')
        : fm('contact.recentlyDeleted.confirmRestore.title', 'Restore project?');
    const restoreConfirmMessage = pendingRestore?.type === 'client' ? (
        <FormattedMessage
            id="contact.recentlyDeleted.confirmRestore.messageClient"
            defaultMessage="Restore this client? The client and every project that was deleted together with them will be recovered."
        />
    ) : (
        <FormattedMessage
            id="contact.recentlyDeleted.confirmRestore.message"
            defaultMessage="Restore this project? The project and all its related records will be recovered."
        />
    );

    // Defensive perm check — strip card visibility already gates the path here, but
    // covers the direct-URL edge case if someone routes here some other way later.
    if (!props.permissions || Object.keys(props.permissions).length === 0) {
        return null;
    }
    if (!props.permissions.superAdmin && !props.permissions.fullPrivileges) {
        return null;
    }

    return (
        <>
            <MikaTable
                columns={columns}
                data={records}
                actions={tableActions}
                loading={loading}
                emptyMessage={fm('contact.recentlyDeleted.emptyState', 'No deleted items.')}
            />

            <MikaConfirmModal
                isOpen={!!pendingRestore}
                onClose={cancelRestore}
                onConfirm={confirmRestore}
                title={restoreConfirmTitle}
                message={restoreConfirmMessage}
                confirmLabel={fm('contact.recentlyDeleted.confirmRestore.confirmLabel', 'Restore')}
                confirmColor="primary"
            />
        </>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return {
        projectDeletionActions: bindActionCreators(projectDeletionActions as any, dispatch),
        clientDeletionActions: bindActionCreators(clientDeletionActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(RecentlyDeletedProjects as any)));
